// Generated by GitHub Copilot
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KeyRound } from 'lucide-react'
import type Account from '#core/entities/auth/accounts/Account.ts'
import User from '#core/entities/auth/users/User.ts'
import { startSsoLink, startSsoSignIn } from '#core/auth/sso-sign-in.ts'
import { useComponentState } from '#core/state/State.ts'
import { useUserState } from '#core/state/UserState.ts'
import { MicrosoftRedirectButton } from '#components/entities/users/sso/microsoft/microsoft-redirect-button.tsx'
import { LinkedAccountsState } from './linked-accounts-state.ts'
import { LinkedAccountRemoveDialog } from './linked-account-remove-dialog.tsx'
import { mapLinkedAccountLabel } from './linked-account-label.ts'

/** Card listing the accounts the user signs in with (password, Microsoft), with connecting a Microsoft account and removal. */
export function LinkedAccountsSection({ id }: { id?: string }) {
  const { t } = useTranslation('settings')
  const state = useComponentState(LinkedAccountsState)
  const userState = useUserState()
  const [removing, setRemoving] = useState<Account | null>(null)
  const baseId = useId()
  const titleId = `${baseId}-title`
  const hintId = `${baseId}-only-hint`
  const currentAccountId = userState.user?.account?.id
  const ssoLocked = User.memberCompanies(userState.user).some((company) => company.lockReasons.includes('sso'))
  // The link returns here, to this section, on a fresh page load.
  const returnTo = () => `${window.location.pathname}${id ? `#${id}` : ''}`

  return (
    <section id={id} className="card" aria-labelledby={titleId}>
      <div className="card-body">
        <div className="d-flex align-items-start gap-3">
          <KeyRound size={24} className="text-primary flex-shrink-0 mt-1" aria-hidden="true" />
          <div className="flex-grow-1">
            <h2 id={titleId} className="h6 fw-bold mb-1">{t('security.linkedAccounts.title')}</h2>
            <p className="text-secondary mb-0">{t('security.linkedAccounts.description')}</p>
          </div>
        </div>

        {state.loadFailed && <div className="alert alert-danger mt-3 mb-0" role="alert">{t('security.linkedAccounts.loadError')}</div>}

        {state.linked && (
          <div className="alert alert-success mt-3 mb-0" role="status">
            <p className="mb-0">{t('security.linkedAccounts.linked')}</p>
            {ssoLocked && (
              <>
                <p className="mt-2 mb-2">{t('security.linkedAccounts.stillLocked')}</p>
                <MicrosoftRedirectButton small label={t('auth:sso.signInNow')} start={() => startSsoSignIn('microsoft', '/')} />
              </>
            )}
          </div>
        )}

        {state.lastAccountRefused && (
          <div className="text-danger mt-3" role="alert">{t('security.linkedAccounts.lastAccount')}</div>
        )}

        {state.accounts && (
          <ul className="list-group mt-3">
            {state.accounts.map((account) => {
              const label = mapLinkedAccountLabel(account, t)
              return (
                <li key={account.id} className="list-group-item d-flex align-items-center justify-content-between gap-3">
                  <div className="min-w-0">
                    <div className="fw-medium">{label.title}</div>
                    <div className="text-secondary small text-break">{label.detail}</div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger flex-shrink-0"
                    disabled={state.onlyAccount}
                    aria-describedby={state.onlyAccount ? hintId : undefined}
                    aria-label={t('security.linkedAccounts.removeLabel', { account: label.title })}
                    onClick={() => setRemoving(account)}
                  >
                    {t('shared.actions.remove')}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
        {state.accounts && state.onlyAccount && (
          <p id={hintId} className="text-secondary small mt-2 mb-0">{t('security.linkedAccounts.onlyAccount')}</p>
        )}

        {state.accounts && (
          <MicrosoftRedirectButton
            className="mt-3"
            label={t('security.linkedAccounts.connect')}
            start={() => startSsoLink('microsoft', returnTo())}
          />
        )}
      </div>

      <LinkedAccountRemoveDialog
        account={removing}
        isCurrent={removing !== null && removing.id === currentAccountId}
        state={state}
        onClose={() => setRemoving(null)}
      />
    </section>
  )
}
